"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { InviteForm } from "./InviteForm";

interface Member {
  id: string;
  channelId: string;
  memberType: string;
  userId: string | null;
  agentId: string | null;
  role: string;
  joinedAt: string;
  invitedName: string | null;
  invitedBy: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

interface PendingInvite {
  id: string;
  name: string;
  phone: string;
  code: string;
  createdAt: string;
}

interface Props {
  channelId: string;
  channelName: string | null;
  isCreator: boolean;
  onClose: () => void;
  onOpenTerminal?: () => void;
}

export function MembersPanel({ channelId, channelName, isCreator, onClose, onOpenTerminal }: Props) {
  const { token, user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchData = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) return;

    try {
      const [membersData, invitesData] = await Promise.all([
        apiFetch<Member[]>(`/channels/${channelId}/members`, t),
        isCreator
          ? apiFetch<PendingInvite[]>(`/channels/${channelId}/invites`, t)
          : Promise.resolve([]),
      ]);
      setMembers(membersData);
      setInvites(invitesData);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [channelId, isCreator]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRevoke = async (inviteId: string) => {
    const t = tokenRef.current;
    if (!t) return;

    setRevoking(inviteId);
    try {
      await apiFetch(`/channels/${channelId}/invites/${inviteId}`, t, {
        method: "DELETE",
      });
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch {
      // ignore
    } finally {
      setRevoking(null);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    const t = tokenRef.current;
    if (!t) return;

    setRemoving(memberId);
    try {
      await apiFetch(`/channels/${channelId}/members/${memberId}`, t, {
        method: "DELETE",
      });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch {
      // ignore
    } finally {
      setRemoving(null);
    }
  };

  // Determine the current user's role in this channel
  const myMembership = members.find((m) => m.userId === user?.id);
  const canRemoveMembers = myMembership?.role === "owner" || myMembership?.role === "admin";

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div style={isMobile ? fullScreenStyle : sidePanelStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.85rem", color: "var(--color-text)", fontWeight: 500 }}>
            Members
          </div>
          <div style={{ fontSize: "0.7rem", color: "var(--color-text-tertiary)", marginTop: 2 }}>
            {channelName ?? "Channel"}
          </div>
        </div>
        <button onClick={onClose} style={closeButtonStyle} title="Close">
          &times;
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "0.75rem" }}>
        {loading ? (
          <div style={{ color: "var(--color-text-faint)", fontSize: "0.8rem", textAlign: "center", padding: "2rem 0" }}>
            Loading...
          </div>
        ) : (
          <>
            {/* Members list */}
            <div style={{ marginBottom: "1rem" }}>
              {members.map((m) => {
                const isYou = m.userId === user?.id;
                const name = isYou
                  ? (m.displayName ?? "You")
                  : (m.displayName ?? m.invitedName ?? memberLabel(m));

                return (
                  <div key={m.id} style={memberRowStyle}>
                    {m.avatarUrl ? (
                      <img
                        src={m.avatarUrl}
                        alt=""
                        style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                        background: m.memberType === "agent" ? "#e8f5e8" : "#e8e8f5",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.7rem", color: "var(--color-text-tertiary)",
                      }}>
                        {m.memberType === "agent" ? "🤖" : name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.8rem", color: "var(--color-text)" }}>
                        {name}
                        {isYou && <span style={{ color: "var(--color-text-tertiary)", marginLeft: 4 }}>(you)</span>}
                      </div>
                      <div style={{ fontSize: "0.65rem", color: "var(--color-text-faint)" }}>
                        {m.role}{m.memberType === "agent" ? " \u00b7 agent" : ""}
                      </div>
                    </div>
                    {canRemoveMembers && !isYou && m.role !== "owner" && (
                      <button
                        onClick={() => handleRemoveMember(m.id)}
                        disabled={removing === m.id}
                        style={revokeButtonStyle}
                        title="Remove member"
                      >
                        {removing === m.id ? "..." : "Remove"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Terminal access — creator only, when channel has an agent */}
            {isCreator && members.some((m) => m.memberType === "agent") && (
              <>
                <div style={sectionDividerStyle} />
                <div style={{ marginBottom: "1rem" }}>
                  <div style={sectionLabelStyle}>Terminal</div>
                  <button
                    onClick={onOpenTerminal}
                    style={terminalLinkStyle}
                  >
                    <span style={{ fontSize: "1rem" }}>⬛</span>
                    <span>Open Terminal</span>
                  </button>
                </div>
              </>
            )}

            {/* Invite section — creator only */}
            {isCreator && (
              <>
                <div style={sectionDividerStyle} />

                {/* Pending invites */}
                {invites.length > 0 && (
                  <div style={{ marginBottom: "1rem" }}>
                    <div style={sectionLabelStyle}>Pending Invites</div>
                    {invites.map((inv) => (
                      <div key={inv.id} style={memberRowStyle}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "0.8rem", color: "var(--color-text)" }}>
                            {inv.name}
                          </div>
                          <div style={{ fontSize: "0.65rem", color: "var(--color-text-faint)" }}>
                            {inv.phone}
                          </div>
                          <div style={{ fontSize: "0.65rem", color: "var(--color-text-tertiary)", marginTop: 2 }}>
                            occum.net/i/{inv.code}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevoke(inv.id)}
                          disabled={revoking === inv.id}
                          style={revokeButtonStyle}
                          title="Revoke invite"
                        >
                          {revoking === inv.id ? "..." : "Revoke"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Invite button / form */}
                {showInviteForm ? (
                  <div>
                    <div style={sectionLabelStyle}>Invite someone</div>
                    <InviteForm
                      channelId={channelId}
                      onInviteSent={() => {
                        fetchData();
                        setShowInviteForm(false);
                      }}
                    />
                    <button
                      onClick={() => setShowInviteForm(false)}
                      style={{ ...revokeButtonStyle, marginTop: 8, display: "block" }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowInviteForm(true)}
                    style={inviteButtonStyle}
                  >
                    + Invite
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function memberLabel(m: Member): string {
  if (m.memberType === "agent") return `Agent`;
  if (m.userId) return `User #${m.userId.slice(0, 8)}`;
  return "Unknown";
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const sidePanelStyle: React.CSSProperties = {
  width: 300,
  minWidth: 300,
  maxWidth: 300,
  display: "flex",
  flexDirection: "column",
  background: "var(--color-bg-secondary)",
  borderLeft: "1px solid var(--color-border-subtle)",
  height: "100%",
  overflow: "hidden",
};

const fullScreenStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  background: "var(--color-bg)",
  zIndex: 100,
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "0.75rem",
  borderBottom: "1px solid var(--color-border-subtle)",
  gap: 8,
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-text-tertiary)",
  fontSize: "1.2rem",
  cursor: "pointer",
  padding: "0.25rem",
  lineHeight: 1,
};

const memberRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "0.5rem 0",
  gap: 8,
};

const sectionDividerStyle: React.CSSProperties = {
  borderTop: "1px solid var(--color-border-subtle)",
  margin: "0.5rem 0",
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  marginBottom: 8,
};

const revokeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--color-border-secondary)",
  borderRadius: 4,
  padding: "0.2rem 0.5rem",
  fontSize: "0.7rem",
  color: "var(--color-text-muted)",
  cursor: "pointer",
};

const inviteButtonStyle: React.CSSProperties = {
  background: "var(--color-bg-input)",
  border: "1px solid var(--color-border-secondary)",
  borderRadius: 6,
  padding: "0.5rem 1rem",
  fontSize: "0.8rem",
  color: "var(--color-text)",
  cursor: "pointer",
  width: "100%",
};

const terminalLinkStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0.5rem 0.75rem",
  background: "#1a1a1a",
  color: "#e0e0e0",
  border: "none",
  borderRadius: 6,
  fontSize: "0.8rem",
  cursor: "pointer",
  width: "100%",
};
